/**
 * Settings → Project: the name and description (editable with
 * `projects:write`), "Leave project" for anyone in it, and a danger zone with "Delete project" for those holding `projects:delete`.
 */

import { useState } from "react";
import { Box, BoxHeader } from "@/components/ui/box";
import { Button } from "@/components/ui/button";
import { Field } from "@/components/ui/field";
import { errorText } from "@/components/ui/states";
import { useProject, useUpdateProject } from "@/features/projects/hooks";
import type { ProjectDetail } from "@/features/projects/types";
import { useUiStore } from "@/stores/ui-store";
import { DeleteProjectDialog } from "./delete-project-dialog";
import { LeaveProjectButton } from "./leave-project-button";

const NO_RENAME = "Only owners and admins can rename.";

function GeneralForm({ project, editable }: { project: ProjectDetail; editable: boolean }) {
  const [name, setName] = useState(project.name);
  const [description, setDescription] = useState(project.description);
  // Follow a rename made elsewhere (another tab, a teammate) until you start editing.
  const [seen, setSeen] = useState({ name: project.name, description: project.description });
  if (seen.name !== project.name || seen.description !== project.description) {
    setSeen({ name: project.name, description: project.description });
    if (name === seen.name) setName(project.name);
    if (description === seen.description) setDescription(project.description);
  }
  const update = useUpdateProject();
  const notify = useUiStore((s) => s.notify);
  const changed = name.trim() !== project.name || description.trim() !== project.description;
  const save = () =>
    update.mutate(
      { name: name.trim(), description: description.trim() },
      {
        onSuccess: (saved) => (setName(saved.name), setDescription(saved.description), notify("Saved.")),
        onError: (e) => notify(errorText(e, "Could not save the project."), "error"),
      },
    );
  return (
    <form className="flex flex-col gap-4 p-4" onSubmit={(e) => (e.preventDefault(), save())}>
      <Field label="Project name" htmlFor="project-name-edit" hint={editable ? undefined : NO_RENAME}>
        <input
          id="project-name-edit"
          className="inp"
          value={name}
          disabled={!editable}
          title={editable ? undefined : NO_RENAME}
          onChange={(e) => setName(e.target.value)}
        />
      </Field>
      <Field label="Description" htmlFor="project-description-edit" optional>
        <textarea
          id="project-description-edit"
          className="inp min-h-[72px] py-2"
          value={description}
          disabled={!editable}
          placeholder={editable ? "What this project is for" : undefined}
          onChange={(e) => setDescription(e.target.value)}
        />
      </Field>
      {editable && (
        <div>
          <Button type="submit" variant="primary" disabled={!name.trim() || !changed || update.isPending}>
            Save
          </Button>
        </div>
      )}
    </form>
  );
}

export function ProjectTab() {
  const { project, can } = useProject();
  const [deleting, setDeleting] = useState(false);
  if (!project) return null;
  return (
    <>
      <Box>
        <BoxHeader title="General" />
        <GeneralForm project={project} editable={can("projects:write")} />
      </Box>
      <Box>
        <LeaveProjectButton />
      </Box>
      {can("projects:delete") && (
        <Box>
          <BoxHeader title="Danger zone" />
          <div className="flex items-center justify-between gap-4 p-4">
            <div className="flex flex-col gap-0.5">
              <span className="text-sm font-semibold">Delete project</span>
              <span className="text-[13px] text-muted">
                Deletes the project and everything in it: datasets, evaluators, experiments and review queues.
                Running experiments stop.
              </span>
            </div>
            <Button onClick={() => setDeleting(true)}>Delete project</Button>
          </div>
        </Box>
      )}
      {deleting && <DeleteProjectDialog project={project} onClose={() => setDeleting(false)} />}
    </>
  );
}
